/**
 * API Configuration
 * Centralized API base URL configuration
 * 

 * Default order:
 * 1. VITE_API_BASE_URL if provided
 * 2. Local backend in development
 * 3. VITE_API_BASE_URL_FALLBACK if provided
 * 4. https://ecourt.n2nai.io/backend-api
 */

const DEFAULT_LOCAL_API_BASE_URL = 'http://127.0.0.1:8000/backend-api'
const DEFAULT_REMOTE_API_BASE_URL = 'https://ecourt.n2nai.io/backend-api'


function normalizeBaseUrl(url: string): string {
  return url.trim().replace(/\/+$/, '')
}

function canUseBaseUrl(url: string): boolean {
  if (typeof window === 'undefined') return true
  if (window.location.protocol === 'https:' && url.startsWith('http://')) {
    return false
  }
  return true
}

function uniqueUrls(urls: Array<string | null | undefined>): string[] {
  const seen = new Set<string>()
  const result: string[] = []
  for (const url of urls) {
    if (!url) continue
    const normalized = normalizeBaseUrl(url)
    if (!normalized || !canUseBaseUrl(normalized) || seen.has(normalized)) continue
    seen.add(normalized)
    result.push(normalized)
  }
  return result
}

export function getApiBaseUrlCandidates(): string[] {
  const envBaseUrl = import.meta.env.VITE_API_BASE_URL?.trim()
  const envFallbackBaseUrl = import.meta.env.VITE_API_BASE_URL_FALLBACK?.trim()
  const isLocalDev =
    typeof window !== 'undefined' &&
    window.location.protocol !== 'https:' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')

  return uniqueUrls([
    envBaseUrl,
    isLocalDev ? DEFAULT_LOCAL_API_BASE_URL : null,
    envFallbackBaseUrl,
    DEFAULT_REMOTE_API_BASE_URL,
  ])
}

export function buildApiUrl(baseUrl: string, path: string): string {
  const cleanBase = normalizeBaseUrl(baseUrl)
  const cleanPath = path.startsWith('/') ? path : `/${path}`
  return `${cleanBase}${cleanPath}`
}

async function isBaseUrlReachable(baseUrl: string): Promise<boolean> {
  try {
    const controller = new AbortController()
    const timeout = window.setTimeout(() => controller.abort(), 1200)
    const response = await fetch(buildApiUrl(baseUrl, '/health'), {
      method: 'GET',
      signal: controller.signal,
      cache: 'no-store',
    })
    window.clearTimeout(timeout)
    return response.ok
  } catch {
    return false
  }
}

export async function resolveApiBaseUrl(): Promise<string> {
  const candidates = getApiBaseUrlCandidates()
  for (const candidate of candidates) {
    if (await isBaseUrlReachable(candidate)) {
      return candidate
    }
  }
  return candidates[0] || DEFAULT_REMOTE_API_BASE_URL
}

export const API_BASE_URL = getApiBaseUrlCandidates()[0] || DEFAULT_REMOTE_API_BASE_URL

// Log for debugging (only in development)
if (import.meta.env.DEV) {
  console.log('🌐 API Base URL candidates:', getApiBaseUrlCandidates())
}
